import React, { useEffect, useRef, useState } from 'react';

import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import AddIcon from '@mui/icons-material/Add';

import Button from 'shared/atoms/Button';
import Menu from 'shared/molecules/Menu';

import T from 'shared/atoms/Typography';

import theme from 'shared/themes/default';

import PlaylistContent from './components/PlaylistContent';
import PlaylistTitleBoxList from './components/PlaylistTitleBoxList';

import { usePlaylist } from '../../context';

const HeadingCounterWrapper = styled.div`
  display: flex;
  column-gap: ${theme.spacing.m2};
`;

const HeadingCounter = styled.div`
  display: flex;
  column-gap: ${theme.spacing.s};
`;

const StyledOuterWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledTitleBlock = styled.div`
  margin-top: 24px;
`;

const StyledPlaylistWrapper = styled.div`
  display: flex;
`;

const StyledPlaylist = styled.div`
  flex: 50%;
  max-width: 50%;
  flex-grow: 0;
  padding-right: 1.375rem;
  ${theme.breakpoints.mobile} {
    flex: 100%;
    max-width: 100%;
    padding-right: 0;
  }
`;

const StyledPlaylistTitleBlock = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 20px;
  min-height: 40px;
  ${theme.breakpoints.tablet} {
    flex-direction: column;
    align-items: flex-start;
    min-height: 90px;
  }
  ${theme.breakpoints.mobile} {
    min-height: 72px;
  }
`;

const PlaylistOverview = () => {
  const {
    canManagePlaylists,
    canSortPlaylists,
    hasSubscription,
    hasVideocationCatalogAccess,
    isGlobalOrganization,
    managerSelected,
    playlistId,
    dialogs: {
      openPlaylistForm,
      scrollToSelected,
      setOpenCopyPlaylistForm,
      setOpenPlaylistForm,
      setScrollToSelected,
    },
    playlists: {
      listOwnedPlaylists,
      loadingPlaylists,
      selectedPlaylist,
      selectPlaylistHandler,
    },
  } = usePlaylist();

  const [anchorEl, setAnchorEl] = useState(null);
  const selectedRef = useRef(null);

  const { t } = useTranslation();

  const createPlaylistMenuItems = [
    {
      label: t('playlist.createNewPlaylist'),
      handler: () => setOpenPlaylistForm(true),
    },
  ];

  // TODO: Replace with catalog equivalents
  // if (!organization.lockVcCourses && !organization.hideVcCourses) {

  createPlaylistMenuItems.push({
    label: t('playlist.addExistingPlaylist'),
    handler: () => setOpenCopyPlaylistForm(true),
  });

  useEffect(() => {
    if (playlistId && !loadingPlaylists) {
      selectPlaylistHandler(playlistId);
    }
  }, [loadingPlaylists]);

  // We scroll here to make sure we scroll after the modal is closed
  useEffect(() => {
    if (scrollToSelected) {
      selectPlaylistHandler(scrollToSelected);
      selectedRef?.current?.scrollIntoView({ behavior: 'smooth' });
      setScrollToSelected(null);
    }
  }, [openPlaylistForm]);

  const CustomMenuHandler = (event) => {
    setAnchorEl(anchorEl === null ? event.currentTarget : null);
  };

  const showMenu = !isGlobalOrganization && hasVideocationCatalogAccess;
  const buttonLabel = showMenu
    ? t('playlist.createPlaylist')
    : t('playlist.createNew');
  const buttonAction = showMenu
    ? CustomMenuHandler
    : () => setOpenPlaylistForm(true);

  const noOfPlaylists = listOwnedPlaylists ? listOwnedPlaylists.length : 0;

  return (
    <StyledOuterWrapper>
      {/* Page title block */}
      <StyledTitleBlock>
        {listOwnedPlaylists &&
          listOwnedPlaylists.length === 0 &&
          canManagePlaylists &&
          (isGlobalOrganization || managerSelected !== 'all') && (
            <>
              <T
                color={theme.palette.lightPure}
                fontWeight="medium"
                variant="subtitle"
              >
                {t('playlist.newPlaylist')}
              </T>
              <T
                color={theme.palette.lightPure}
                fontWeight="medium"
                sx={{ mt: theme.spacing.m1 }}
                variant="paragraph"
              >
                {t('playlist.sharePlaylist')}
              </T>
              <Button
                ariaLabel={buttonLabel}
                disabled={!hasSubscription}
                iconRight={AddIcon}
                onClick={buttonAction}
                sx={{
                  width: '100%',
                  maxWidth: '300px',
                  margin: '10px 0',
                  justifyContent: 'space-between',
                }}
                theme="dark"
                type="secondary"
              >
                {buttonLabel}
              </Button>
              <Menu
                freeMenu
                initAnchorEl={anchorEl}
                menuItems={createPlaylistMenuItems}
                setInitAnchorEl={setAnchorEl}
              />
            </>
          )}
      </StyledTitleBlock>

      {/* Playlists and content block */}
      {listOwnedPlaylists && listOwnedPlaylists.length > 0 && (
        <StyledPlaylistWrapper>
          {/* Playlists list block */}
          <StyledPlaylist>
            <StyledPlaylistTitleBlock>
              <HeadingCounterWrapper>
                <HeadingCounter>
                  <T variant="helperText" color={theme.palette.lightPure}>
                    {`${t('playlist.noOfPlaylists')}:`}
                  </T>
                  <T
                    variant="helperText"
                    color={theme.palette.lightPure}
                    fontWeight="bold"
                  >
                    {noOfPlaylists}
                  </T>
                </HeadingCounter>
              </HeadingCounterWrapper>
              {canManagePlaylists &&
                (isGlobalOrganization || managerSelected !== 'all') && (
                  <Button
                    ariaLabel={buttonLabel}
                    disabled={!hasSubscription}
                    iconRight={AddIcon}
                    onClick={buttonAction}
                    sx={{
                      [theme.breakpoints.tablet]: {
                        width: '100%',
                        maxWidth: '300px',
                        justifyContent: 'space-between',
                      },
                    }}
                    theme="dark"
                    type="secondaryOutline"
                  >
                    {buttonLabel}
                  </Button>
                )}
            </StyledPlaylistTitleBlock>
            <Menu
              freeMenu
              initAnchorEl={anchorEl}
              menuItems={createPlaylistMenuItems}
              setInitAnchorEl={setAnchorEl}
            />
            <PlaylistTitleBoxList
              canManagePlaylists={canManagePlaylists}
              isSortable={canSortPlaylists}
              selectedRef={selectedRef}
            />
          </StyledPlaylist>

          {/* Playlists content block */}
          {selectedPlaylist && <PlaylistContent />}
        </StyledPlaylistWrapper>
      )}
    </StyledOuterWrapper>
  );
};

export default PlaylistOverview;
